import { useEffect, useRef, useState } from 'react';
import sendMyCarUpdate from '../../../../api/bank/sendMyCarUpdate';
import Button from '../../../../components/Button/Button';
import alert from '../../../../helpers/alert';
import extractErrorMessage from '../../../../utils/extractErrorMessage';

// Коды типов документов MyCar (документация MCFLeadGenerationService B2C 09.2026).
// «Другой документ» первым: чаще всего банк просит именно то, чего нет в списке.
export const MYCAR_DOCUMENT_TYPES = [
  { value: 'OtherDocumentsLeadGenerator', label: 'Другой документ' },
  { value: 'StatementCertificate', label: 'Выписка' },
  { value: 'ApplicationForm', label: 'Анкета' },
  { value: 'CreditBureauAgreement', label: 'Согласие на сбор и обработку данных' },
  { value: 'PhotoClient', label: 'Фото клиента' },
  { value: 'RegistrationCertificate', label: 'Техпаспорт' },
  { value: 'VehicleVerificationReport', label: 'Акт верификации транспортного средства' },
];

/** Текст, который ставим заявке после отправки доработки. */
export const MYCAR_REWORK_SENT = 'Доработка отправлена в MyCar, ждём ответ банка';

/**
 * Доработка ушла, решение банка ещё не пришло. Пока так — банк не подтвердил
 * условия, и выбирать предложение рано: оно может измениться.
 */
export const awaitingMyCarRework = (appeal, mycarBankId) =>
  appeal?.bank?.id === mycarBankId && (appeal?.errorMessage || '').startsWith(MYCAR_REWORK_SENT);

/**
 * Заявка MyCar ждёт доработки: банк прислал «Одобрено с доработкой» и ждёт документ.
 * Изменение условий («Одобрено с условием», альтернатива) сюда не относится: банк
 * (Аян, 24.09.2026) подтверждает его выбором предложения — PostClientChoise.
 */
export const needsMyCarUpdate = (appeal, mycarBankId) =>
  appeal?.bank?.id === mycarBankId && /доработк/i.test(appeal?.errorMessage || '');

/** Банк одобрил на своих условиях: менеджеру достаточно выбрать предложение. */
export const isMyCarConditionCase = (appeal, mycarBankId) =>
  appeal?.bank?.id === mycarBankId && /с условием/i.test(appeal?.errorMessage || '');

const MyCarUpdateApplication = ({ appealId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState('');
  const [documentType, setDocumentType] = useState(MYCAR_DOCUMENT_TYPES[0].value);
  const [loading, setLoading] = useState(false);

  // Ответ банка приходит колбэком, сам по себе экран его не покажет: менеджеру
  // пришлось бы обновлять страницу. После отправки доработки тихо перечитываем
  // карточку раз в минуту в течение 20 минут. Чаще незачем: банк отвечает
  // минутами, а каждый опрос тянет всю карточку сделки.
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);
  const startPolling = () => {
    clearInterval(pollRef.current);
    let left = 20;
    pollRef.current = setInterval(() => {
      if (left-- <= 0) {
        clearInterval(pollRef.current);
        return;
      }
      onSuccess && onSuccess();
    }, 60000);
  };

  const send = async () => {
    if (!file && !comment.trim()) {
      alert.error('Приложите документ или напишите комментарий для банка');
      return;
    }
    setLoading(true);
    try {
      await sendMyCarUpdate({ appealId, comment: comment.trim(), documentType, file });
      alert.success('Доработка отправлена в MyCar');
      setFile(null);
      setComment('');
      onSuccess && onSuccess();
      startPolling();
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || 'Не удалось отправить доработку в MyCar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#FFF4E5', padding: '15px 20px', marginBottom: 20 }}>
      <p style={{ fontWeight: 600, marginBottom: 5 }}>Доработка по заявке MyCar</p>
      <p style={{ fontSize: 13, marginBottom: 10 }}>
        Банк одобрил заявку и просит дослать документ. Выберите тип, приложите файл и при
        необходимости напишите комментарий — всё уйдёт в MyCar по этой же заявке.
      </p>
      <select
        aria-label="Тип документа"
        value={documentType}
        onChange={(e) => setDocumentType(e.target.value)}
        style={{ display: 'block', marginBottom: 10, padding: 8, minWidth: 280 }}
      >
        {MYCAR_DOCUMENT_TYPES.map((type) => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
      <input
        type="file"
        aria-label="Документ для банка"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        style={{ display: 'block', marginBottom: 10 }}
      />
      <textarea
        aria-label="Комментарий для банка"
        placeholder="Комментарий для банка (необязательно)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        style={{ display: 'block', width: '100%', maxWidth: 520, minHeight: 60, marginBottom: 10, padding: 8 }}
      />
      <Button
        fill
        disabled={loading}
        onClick={send}
        text={
          loading
            ? 'Отправляем…'
            : 'Отправить доработку в MyCar'
        }
      />
    </div>
  );
};

export default MyCarUpdateApplication;
